// src/pages/feedback/FeedbackTicketsList.js
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useFeedbackTickets from '../../component/hooks/useFeedbackTickets';
import FeedbackStatusChip from '../../component/feedback/FeedbackStatusChip';
import {
  FEEDBACK_CATEGORY_OPTIONS,
  FEEDBACK_STATUS_OPTIONS,
} from '../../constans/feedback';

const formatDate = (value) => {
  if (!value) return '-';
  const date = dayjs(value);
  if (!date.isValid()) return '-';
  return date.format('DD.MM.YYYY HH:mm');
};

const categoryLabel = (code) => {
  if (!code) return '-';
  const item = FEEDBACK_CATEGORY_OPTIONS.find((opt) => opt.value === code);
  return item ? item.label : code;
};

export default function FeedbackTicketsList() {
  const navigate = useNavigate();
  const {
    rows,
    total,
    loading,
    error,
    filters,
    pagination,
    setPagination,
    applyFilters,
    resetFilters,
    refresh,
  } = useFeedbackTickets();

  const [emailInput, setEmailInput] = useState(filters.email ?? '');

  useEffect(() => {
    setEmailInput(filters.email ?? '');
  }, [filters.email]);

  useEffect(() => {
    const trimmed = emailInput.trim();
    if (trimmed === (filters.email ?? '')) return;
    const handle = setTimeout(() => {
      applyFilters({ email: trimmed });
    }, 400);
    return () => clearTimeout(handle);
  }, [emailInput, filters.email, applyFilters]);

  const columns = useMemo(() => ([
    {
      field: 'subject',
      headerName: 'Konu',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
          {params.value || '—'}
        </Typography>
      ),
      sortable: false,
    },
    {
      field: 'category',
      headerName: 'Kategori',
      flex: 0.9,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {categoryLabel(params.row?.category)}
        </Typography>
      ),
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => <FeedbackStatusChip status={params.value} />,
      sortable: false,
    },
  ]), []);

  const handlePaginationChange = (next) => {
    setPagination({
      page: next.page,
      pageSize: next.pageSize,
    });
  };

  const handleReset = () => {
    setEmailInput('');
    applyFilters({ status: '', email: '' });
    resetFilters();
  };

  const handleRowClick = (params) => {
    if (!params?.row?.id) return;
    navigate(`/admin/feedback-tickets/${params.row.id}`);
  };

  const renderStatusValue = (value) => {
    if (!value) {
      return <Typography sx={{ color: 'text.disabled' }}>Tüm durumlar</Typography>;
    }
    const option = FEEDBACK_STATUS_OPTIONS.find((opt) => opt.value === value);
    return option?.label || value;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Geri Bildirim Talepleri</Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteri geri bildirimlerini görüntüleyip anlık olarak yanıtlayabilirsiniz.
          </Typography>
        </Box>
        <IconButton onClick={refresh} aria-label="Yenile" size="small" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fafbfd', borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Müşteri e-postasında ara"
              placeholder="ornek@firma.com"
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 360 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="feedback-status-filter" shrink>Durum</InputLabel>
              <Select
                labelId="feedback-status-filter"
                input={<OutlinedInput label="Durum" />}
                value={filters.status}
                onChange={(event) => applyFilters({ status: event.target.value })}
                displayEmpty
                renderValue={renderStatusValue}
              >
                <MenuItem value="">
                  <em>Tüm durumlar</em>
                </MenuItem>
                {FEEDBACK_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="outlined" onClick={handleReset} sx={{ ml: { md: 'auto' }, whiteSpace: 'nowrap' }}>
              Filtreleri Sıfırla
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ height: 560, position: 'relative' }}>
        {loading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              background: 'rgba(255,255,255,0.6)',
            }}
          >
            <CircularProgress size={32} />
          </Stack>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={total}
          paginationModel={pagination}
          onPaginationModelChange={handlePaginationChange}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100, 200]}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          sx={{
            height: '100%',
            '& .MuiDataGrid-row.unread': {
              bgcolor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
          getRowClassName={(params) => {
            if (!params?.row) return '';
            return params.row.unreadCount > 0 ? 'unread' : '';
          }}
          localeText={{
            noRowsLabel: loading ? '' : 'Henüz geri bildirim bileti yok.',
          }}
        />
      </Paper>

      {!rows.length && !loading && !error && (
        <Paper elevation={0} sx={{ mt: 3, p: 4, textAlign: 'center', bgcolor: '#f6f8fb', borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            Henüz geri bildirim bileti yok
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteriler geri bildirim gönderdikçe konuşmalar burada listelenecek.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
